'use client'

import { Mail, MessageCircle, Phone, Star } from 'lucide-react'
import { QuestionCircleIcon } from '@/components/icons/QuestionCircleIcon'

import { ListIcon } from '@/components/icons/ListIcon'
import { MapPinIcon } from '@/components/icons/MapPinIcon'
import { NewspaperIcon } from '@/components/icons/NewspaperIcon'
import React from 'react'

import type { Header as HeaderType, Media } from '@/payload-types'

import { NavArrowIcon } from '@/components/icons/NavArrowIcon'
import { CMSLink } from '@/components/Link'
import { cn } from '@/utilities/ui'
import { getMediaUrl } from '@/utilities/getMediaUrl'

const dropdownItemIconClassName = 'h-[22px] w-[22px] shrink-0 stroke-2 text-black'

function isContactItem(label: string | null | undefined): boolean {
  return Boolean(label?.toLowerCase().includes('contact'))
}

function NavArrow() {
  return (
    <span className="inline-flex shrink-0 text-black" aria-hidden>
      <NavArrowIcon />
    </span>
  )
}

function DropdownItemIcon({
  icon,
  iconUpload,
  iconClassName,
}: {
  icon?: string | null
  iconUpload?: number | Media | null
  iconClassName?: string
}) {
  // Custom uploaded icon takes priority
  if (iconUpload && typeof iconUpload === 'object' && 'url' in iconUpload) {
    const media = iconUpload as Media
    const raw = media.url ?? (media.filename ? `/media/${media.filename}` : '')
    const src = raw ? getMediaUrl(raw) : ''
    if (src) {
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src} alt={media.alt ?? ''} aria-hidden className="h-[22px] w-[22px] shrink-0 object-contain" />
      )
    }
  }

  const cls = iconClassName ?? dropdownItemIconClassName
  switch (icon) {
    case 'phone':
      return <Phone aria-hidden className={cls} />
    case 'mail':
      return <Mail aria-hidden className={cls} />
    case 'messageCircle':
      return <MessageCircle aria-hidden className={cls} />
    case 'circleHelp':
      return <QuestionCircleIcon aria-hidden className={cls} />
    case 'mapPin':
      return <MapPinIcon className={cls} />
    case 'star':
      return <Star aria-hidden className={cls} />
    case 'list':
      return <ListIcon className={cls} />
    case 'newspaper':
      return <NewspaperIcon className={cls} />
    case 'arrowRight':
      return <NavArrow />
    default:
      return <NavArrow />
  }
}

type DropdownItems = NonNullable<NonNullable<HeaderType['navItems']>[number]['dropdownItems']>

function ContactUsDropdown({ dropdown, isOpen }: { dropdown: DropdownItems; isOpen: boolean }) {
  return (
    <div
      className={cn(
        'absolute left-1/2 top-full z-[999] mt-3 w-max -translate-x-1/2',
        'bg-white p-[10px]',
        'transition-opacity duration-200 ease-out motion-reduce:transition-none',
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
      )}
      role="menu"
      aria-hidden={!isOpen}
      style={{ boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 3px 0px' }}
    >
      <div className="flex flex-row items-stretch gap-[20px]">
        {dropdown.map((item, j) => (
          <div
            key={item.id ?? j}
            style={{
              transition: 'transform 600ms ease-out, opacity 600ms ease-out',
              transitionDelay: '0ms',
              transform: isOpen ? 'translateY(0)' : 'translateY(18px)',
              opacity: isOpen ? 1 : 0,
            }}
          >
            <CMSLink
              {...item.link}
              appearance="inline"
              className="font-outfit group flex h-full items-center gap-2 p-[10px] text-[20px] font-bold whitespace-nowrap no-underline text-[#1D1D1B] transition-colors duration-150 hover:bg-[#009BAB] hover:text-white [&_svg]:text-[#1D1D1B] [&_svg]:transition-colors [&_svg]:duration-150 hover:[&_svg]:text-white [&_img]:transition-[filter] [&_img]:duration-150 hover:[&_img]:brightness-0 hover:[&_img]:invert"
            >
              <DropdownItemIcon
                icon={(item as { icon?: string | null }).icon}
                iconUpload={(item as { iconUpload?: number | Media | null }).iconUpload}
                iconClassName="h-[22px] w-[22px] shrink-0 stroke-2"
              />
              {item.link.label}
            </CMSLink>
          </div>
        ))}
      </div>
    </div>
  )
}

const linkClassName = cn(
  'flex min-w-0 items-center justify-start gap-[5px] whitespace-nowrap text-left',
  'text-[20px] font-bold leading-[30px] text-[#1D1D1B] no-underline transition-[color] duration-300',
)

export const HeaderNav: React.FC<{
  data: HeaderType
  setOpenDropdownIndex: (index: number | null) => void
  openDropdownIndex: number | null
}> = ({ data, setOpenDropdownIndex, openDropdownIndex }) => {
  const navItems = data?.navItems || []

  return (
    <nav
      className={cn(
        'flex w-full max-w-full flex-wrap items-center justify-evenly gap-x-4 gap-y-3',
        'md:flex-nowrap md:justify-center md:gap-5 md:self-stretch',
      )}
      aria-label="Main"
    >
      {navItems.map((row, i) => {
        const { link } = row
        const dropdown = row.dropdownItems ?? []
        const hasDropdown = dropdown.length > 0
        const isContact = isContactItem(link.label)

        const openThis = () => {
          if (hasDropdown) setOpenDropdownIndex(i)
          else setOpenDropdownIndex(null)
        }

        return (
          <span key={row.id ?? i} className="flex min-w-0 md:flex-auto">
            <span className="relative inline-flex">
              {link.url || (link.type === 'reference' && link.reference) ? (
                <CMSLink
                  {...link}
                  appearance="inline"
                  className={linkClassName}
                  onFocus={() => {
                    if (hasDropdown) setOpenDropdownIndex(i)
                  }}
                  onMouseOver={openThis}
                  onPointerEnter={openThis}
                >
                  <NavArrow />
                  {link.label}
                </CMSLink>
              ) : (
                <span
                  className={cn(linkClassName, hasDropdown && 'cursor-default')}
                  onFocus={() => {
                    if (hasDropdown) setOpenDropdownIndex(i)
                  }}
                  onMouseOver={openThis}
                  onPointerEnter={openThis}
                >
                  <NavArrow />
                  {link.label}
                </span>
              )}

              {isContact && hasDropdown && (
                <ContactUsDropdown dropdown={dropdown} isOpen={openDropdownIndex === i} />
              )}
            </span>
          </span>
        )
      })}
    </nav>
  )
}

export const HeaderDropdownRow: React.FC<{
  data: HeaderType
  openDropdownIndex: number | null
}> = ({ data, openDropdownIndex }) => {
  const navItems = data?.navItems || []

  return (
    <>
      {navItems.map((row, i) => {
        const dropdown = row?.dropdownItems ?? []
        if (dropdown.length === 0 || isContactItem(row?.link?.label)) return null

        const isOpen = openDropdownIndex === i

        return (
          <div
            key={row.id ?? i}
            className={cn(
              'absolute left-0 top-[80px] z-10 w-full bg-white',
              'transition-opacity duration-200 ease-out motion-reduce:transition-none',
              isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
            aria-label="Submenu"
            aria-hidden={!isOpen}
          >
            <div className="max-w-[1140px] mx-auto px-4 py-3 md:px-7">
              <div className="flex w-full flex-wrap items-center justify-center gap-[20px]">
                {dropdown.map((item, j) => {
                  const { link } = item
                  return (
                    <span
                      key={item.id ?? j}
                      className="inline-flex flex-grow"
                      style={{
                        transition: 'transform 600ms ease-out, opacity 600ms ease-out',
                        transitionDelay: '0ms',
                        transform: isOpen ? 'translateY(0)' : 'translateY(18px)',
                        opacity: isOpen ? 1 : 0,
                      }}
                    >
                      <CMSLink
                        {...link}
                        appearance="inline"
                        className={cn(
                          'flex min-w-0 items-center gap-[5px] whitespace-nowrap',
                          'text-[20px] font-bold leading-[30px] text-[#1D1D1B] no-underline transition-[color] duration-300',
                          'hover:text-[#009BAB]',
                        )}
                      >
                        <DropdownItemIcon
                          icon={(item as { icon?: string | null }).icon}
                          iconUpload={(item as { iconUpload?: number | Media | null }).iconUpload}
                        />
                        {link.label}
                      </CMSLink>
                    </span>
                  )
                })}
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}
